import { useForm } from 'react-hook-form';
import { useState } from 'react';
import { MdVisibility, MdVisibilityOff, MdExpandMore, MdExpandLess } from 'react-icons/md';

import Modal from '../../../Components/Modal/Modal';
import PreviewWorkout from '../../../Components/ProgramWorkout/PreviewWorkout';

import styles from './CalendarPage.module.css';
import TimeOfDayInput from '../../../Components/TimeOfDayInput/TimeOfDayInput';
import { buildWorkoutUpdatePayload } from './workoutUpdatePayload';
import { combineLocalDateTime, splitLocalDateTime } from './scheduling';
import API from '../../../Utils/api';
import { useUser } from '../../../Context/useUser';

export default function UpdateWorkout ({
    data: defaultValues,
    closeCB,
    onSuccess,
    onFailure,
    hideDeleteButton,
    athleteCSS,
    athleteMSS
}) {
    const [showPreview, setShowPreview] = useState(false);
    const { user } = useUser();
    const previewAthleteCSS = athleteCSS ?? defaultValues?.css;
    const previewAthleteMSS = athleteMSS ?? defaultValues?.mss;

    // Convert minutes and seconds to HH:MM:SS format
    const formatDuration = (min, sec) => {
        const totalMinutes = parseInt(min) || 0;
        const seconds = parseInt(sec) || 0;
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    };

    const { register, handleSubmit } = useForm({
        defaultValues: {
            ...defaultValues,
            status: defaultValues.status || 'Assigned',
            workoutDate: splitLocalDateTime(defaultValues.workoutStart).date,
            workoutTime: splitLocalDateTime(defaultValues.workoutStart).time,
            trainingDuration: formatDuration(defaultValues.trainingDurationMin, defaultValues.trainingDurationSec)
        }
    });

    const isCompleted =
        user.type !== 'Admin' &&
        (defaultValues.status === 'Completed' || defaultValues.status === 'Declined');

    const onEventUpdate = async ({
        workoutDate,
        workoutTime,
        trainingDuration,
        volume,
        status
    }) => {
        const workoutStart = combineLocalDateTime(workoutDate, workoutTime);
        if (!workoutStart) {
            onFailure('Please enter a valid start date and time!');
            return;
        }

        if (isCompleted && status === 'Assigned') {
            onFailure('Cannot change a completed or declined workout back to assigned!');
            return;
        }

        // Parse HH:MM:SS format back to minutes and seconds
        if (!trainingDuration || !trainingDuration.includes(':')) {
            onFailure('Please enter training duration in HH:MM:SS format (e.g., 01:30:00)');
            return;
        }

        const parts = trainingDuration.split(':');
        if (parts.length !== 3) {
            onFailure('Please enter training duration in HH:MM:SS format (e.g., 01:30:00)');
            return;
        }

        const hours = parseInt(parts[0]) || 0;
        const minutes = parseInt(parts[1]) || 0;
        const seconds = parseInt(parts[2]) || 0;

        const trainingDurationMin = hours * 60 + minutes;
        const trainingDurationSec = seconds;

        try {
            const { data } = await API.updateWorkout(
                defaultValues._id,
                buildWorkoutUpdatePayload({
                    workoutStart,
                    trainingDurationMin,
                    trainingDurationSec,
                    volume,
                    status
                })
            );
            onSuccess('Workout updated!', data);
        } catch (err) {
            console.error('Update workout error:', err);
            onFailure(err?.response?.data?.message || "Couldn't update workout!");
        }
    };

    const onEventRemove = async workout => {
        if (isCompleted) {
            onFailure('Cannot delete a completed or declined workout!');
            return;
        }
        try {
            await API.removeWorkout(workout._id);
            onSuccess('Workout removed!', defaultValues);
        } catch (err) {
            onFailure("Couldn't remove workout!");
        }
    };

    const togglePreview = () => {
        setShowPreview(!showPreview);
    };

    return (
        <Modal
            closeCB={closeCB}
            title="Update Workout"
            size="large"
            footer={
                <div className={styles.row}>
                    {!hideDeleteButton && (
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => onEventRemove(defaultValues)}
                            style={{
                                backgroundColor: '#dc3545',
                                borderColor: '#dc3545',
                                color: 'white'
                            }}
                        >
                            Remove
                        </button>
                    )}
                    {!isCompleted && (
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleSubmit(onEventUpdate)}
                        >
                            Update
                        </button>
                    )}
                </div>
            }
        >
            <form className={styles.form}>
                <div className="form-group">
                    <label htmlFor="status">Status</label>
                    <select
                        {...register('status', { required: true })}
                        className="form-control"
                        disabled={isCompleted}
                    >
                        <option value="Assigned">Assigned</option>
                        <option value="Completed">Completed</option>
                        <option value="Declined">Declined</option>
                    </select>
                </div>
                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="workoutDate">Starting date</label>
                        <input
                            id="workoutDate"
                            type="date"
                            {...register('workoutDate', { required: true })}
                            className="form-control"
                            disabled={isCompleted}
                            lang="en"
                        />
                    </div>
                    <div className="form-group">
                        <TimeOfDayInput
                            id="workoutTime"
                            label="Starting time"
                            {...register('workoutTime', { required: true })}
                            disabled={isCompleted}
                        />
                    </div>
                </div>
                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="trainingDuration">Training Duration (HH:MM:SS)</label>
                        <input
                            type="text"
                            {...register('trainingDuration', { required: true })}
                            className="form-control"
                            placeholder="e.g., 01:30:00"
                            disabled={isCompleted}
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="volume">Distance</label>
                        <input
                            type="number"
                            {...register('volume', { required: true })}
                            className="form-control"
                            placeholder="Distance"
                            disabled={isCompleted}
                        />
                    </div>
                </div>

                {defaultValues.programGroup && (
                    <div className="form-group">
                        <button
                            type="button"
                            className="btn btn-ghost"
                            onClick={togglePreview}
                            style={{
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 'var(--space-sm)',
                                marginTop: 'var(--space-md)',
                                padding: 'var(--space-md)',
                                border: '1px solid var(--color-border)'
                            }}
                        >
                            {showPreview ? (
                                <>
                                    <MdVisibilityOff size={16} />
                                    Hide Workout Preview
                                    <MdExpandLess size={16} />
                                </>
                            ) : (
                                <>
                                    <MdVisibility size={16} />
                                    Show Workout Preview
                                    <MdExpandMore size={16} />
                                </>
                            )}
                        </button>
                    </div>
                )}

                {showPreview && (
                    <div className="form-group" style={{ marginTop: 'var(--space-lg)' }}>
                        <div className="island padding10">
                            <div className={styles.previewHeader}>
                                <h5 className={styles.previewTitle}>
                                    Workout Program Preview
                                </h5>
                                <p className={styles.previewSubtitle}>
                                    View the assigned program details below
                                </p>
                            </div>
                            <PreviewWorkout
                                isOpen={true}
                                onClose={() => { }}
                                workout={defaultValues}
                                athleteCSS={previewAthleteCSS}
                                athleteMSS={previewAthleteMSS}
                            />
                        </div>
                    </div>
                )}
            </form>
        </Modal>
    );
}
